import { useState } from 'react'
import { Card } from '@/components/shared/Card'
import { Button } from '@/components/shared/Button'
import { RequestCapModal } from './RequestCapModal'
import type { OverflowWindowInfo, PublicOverflowGeo } from '@/types/api'
import { nextResetAt, resetInTrueUTC, resetInViewerTime, viewerIsUTC } from '@/utils/reset-time'

interface PublicOverflowSectionProps {
  geos: PublicOverflowGeo[]
  overflowIsLocked: boolean
  offerSlug?: string
  requestPath?: string
  /** The offer's window payload — carries the pre-day request state (Reco's 9 PM window). */
  overflowWindow?: OverflowWindowInfo | null
  /** The offer's cap unit — Reco requests must ask for a cv count, not a $ amount. */
  capUnit?: 'usd' | 'conversions'
  /** Set when the selected offer is admin-paused — the server refuses every overflow
   *  claim while true, so the request buttons are disabled here instead of only
   *  surfacing the 409 after submit. */
  paused?: boolean
}

function capacityLabel(g: PublicOverflowGeo, paused?: boolean): string {
  if (paused) return 'Paused'
  if (g.is_locked) return 'Locked'
  if (g.pre_day_for) return g.has_capacity ? 'Available' : 'Fully pre-approved'
  if (g.is_over_cap) return 'Cap reached'
  if (!g.has_capacity) return 'Depleted'
  return 'Available'
}

/** Public overflow geos — no pool $ shown (margin-safe). */
export function PublicOverflowSection({ geos, overflowIsLocked, offerSlug, requestPath, overflowWindow, capUnit = 'usd', paused = false }: PublicOverflowSectionProps) {
  const [active, setActive] = useState<PublicOverflowGeo | null>(null)

  if (geos.length === 0) return null

  // Inside the pre-day window (Reco, from 9 PM offer-local) a new request is FOR tomorrow.
  // The server's own instant when the payload has landed; derived from the offer's zone + hour
  // otherwise, so the copy is never blank while the dashboard is still loading.
  const resetAt = overflowWindow?.resets_at_utc
    ? new Date(overflowWindow.resets_at_utc)
    : nextResetAt(overflowWindow?.reset_timezone, overflowWindow?.reset_hour)

  const preRequestForDate = overflowWindow?.pre_request_open
    ? overflowWindow.pre_request_for_local_date ?? null
    : null

  const offerLabel =
    offerSlug === 'testerup'
      ? 'Testerup'
      : offerSlug === 'freecash'
        ? 'Freecash'
        : offerSlug === 'playful'
          ? 'Playful'
          : offerSlug === 'teststar'
            ? 'Teststar'
            : 'This offer'

  return (
    <div className="mb-4">
      <h3 className="text-sm font-semibold text-text-primary mb-1">Public overflow geos</h3>
      <p className="text-xs text-text-muted mb-3">
        Request cap from the shared overflow pool in these geos ({offerLabel}). If you don&apos;t
        have geo access yet, your account manager approves both access and today&apos;s draw in one
        step — overflow resets at the offer&apos;s daily reset{' '}
        {/* Stated in true UTC plus the reader's own clock. The old copy hardcoded "12:00 AM PT",
            which assumed a midnight boundary AND a hardcoded abbreviation that is wrong half the
            year (PT is PDT or PST depending on the date) — and said nothing to an affiliate who
            is in neither zone. */}
        ({resetInTrueUTC(resetAt)}
        {viewerIsUTC(resetAt) ? '' : ` \u00b7 ${resetInViewerTime(resetAt)} your time`}).
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {geos.map((g) => {
          const canRequest = g.has_capacity && !overflowIsLocked && !g.is_locked && !paused
          return (
            <Card key={g.geo_id} className="!p-4 flex flex-col gap-2.5">
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-semibold text-sm">{g.geo_code}</span>
                  {g.geo_name && (
                    <span className="text-xs text-text-muted ml-2">{g.geo_name}</span>
                  )}
                </div>
                <span
                  className={
                    'text-[10px] uppercase tracking-wide font-medium ' +
                    (canRequest ? 'text-brand-green' : 'text-amber-600')
                  }
                >
                  {capacityLabel(g, paused)}
                </span>
              </div>
              <div className="text-xs text-text-muted">
                {g.pre_day_for
                  ? `Tomorrow's pool ${g.pct_used}% pre-approved`
                  : `${g.pct_used}% of today's cap filled`}
              </div>
              {(g.your_pre_approved ?? 0) > 0 && (
                <div className="text-xs font-medium text-brand-green">
                  ✓ You&apos;re approved for {g.your_pre_approved!.toLocaleString('en-US')} tomorrow
                  — starts at the daily reset
                </div>
              )}
              <Button
                variant="secondary"
                className="w-full text-xs mt-auto"
                disabled={!canRequest}
                onClick={() => setActive(g)}
              >
                {preRequestForDate ? 'Request cap for tomorrow' : 'Request overflow cap'}
              </Button>
              {paused && <p className="text-[10px] text-amber-600 text-center">Offer is paused</p>}
            </Card>
          )
        })}
      </div>
      {active && (
        <RequestCapModal
          open
          onClose={() => setActive(null)}
          geoId={active.geo_id}
          geoCode={active.geo_code}
          geoName={active.geo_name ?? active.geo_code}
          requestPath={requestPath}
          requestKind="overflow"
          unit={capUnit}
          title={preRequestForDate ? 'Request cap for tomorrow' : 'Request overflow cap'}
          preRequestForDate={preRequestForDate}
        />
      )}
    </div>
  )
}
